import { PairingShader } from "./shaders/PairingShader";
import { Camera } from "../../../SharedGL/Camera";
import {
  IPairingFlight,
  IDisplayPairing,
  IDisplayBidPairing,
  IDisplayPairingEventGroup,
  IDisplayPositioningFlight,
  INonFlyingPairingAssignment,
  IPairingsLogicModifierGL,
  ICheckInOutOverride,
  IPairingGroundEvent,
  IDisplayBidNFA,
} from "@vmosolutions/oc-interfaces";
import { hexToIRGBA } from "../../../SharedGL/TimelineUtils";
import { PairingBuffer } from "./shaders/PairingBuffer";
import { ITemporaryPairing } from "src/redux/reducers/CrewScheduleReducer";
import { getPairingsInRange, IIndexRange } from "../TimelineUtils";
import { crewBidRowHeight } from "src/components/CrewSchedule/CrewMemberBidding";
import { LightenDarkenColor } from "src/utils";

const pColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  // pColors.push(...[0.063, 0.063, 0.063, 1]); //); //black back ground
  pColors.push(...[0.3, 0.3, 0.3, 1]); //make the pairings have a gray back ground
});

const ineligibleToAwardColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  ineligibleToAwardColors.push(...[1, 0, 0, 0.5]); // used to draw awarded bid pairings that are being unwarded.
});

const dirtyPairingColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  dirtyPairingColors.push(...[0.3, 0.3, 0.3, 1]); // [0.0, 1.0, 0.0, 0.2] used to draw assigned bid pairings in scheduled row.
});

const tempPairingColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  tempPairingColors.push(...[0.5, 0.5, 0.5, 0.25]); //[0.54, 0.55, 0.52, 1.0]);
});

const ghostPairingColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  ghostPairingColors.push(...[0.8, 0.8, 0.8, 1.0]); //[0.54, 0.55, 0.52, 1.0]);
});

const nColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  nColors.push(...[0.0, 1.0, 0.0, 1.0]);
});

const pairingFlightColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  pairingFlightColors.push(...[0.0, 0.0, 1.0, 1.0]);
});

interface IPairingVertexOffset {
  [pairingId: string]: {
    pairingOffset: number;
    pairingVertexCount: number;
    nfaOffset: number;
    nfaVertexCount: number;
  };
}

export enum BidItemStatus {
  assigned_in_stage_row,
  assigned_in_rank_row,
  awarded_in_stage_row,
  awarded_in_rank_row,
  assigned_to_unaward_in_stage_row,
  assigned_to_unaward_in_rank_row,
  not_eligible_to_assign,
  default,
}

/**
 *
 * @param b- return status of current bid paring or nfa.
 */
export const returnBidStatus = (b: IDisplayBidPairing | IDisplayBidNFA): BidItemStatus => {
  switch (true) {
    case b.assigned && !b.awarded && b._id.includes("_staged"): // assigned to award STAGED ROW
      return BidItemStatus.assigned_in_stage_row;
    case b.assigned && !b.awarded && !b._id.includes("_staged"): // asssigned to award
      return BidItemStatus.assigned_in_rank_row;
    case !b.assigned && b.awarded && b._id.includes("_staged"): // awarded STAGED ROW
      return BidItemStatus.awarded_in_stage_row;
    case !b.assigned && b.awarded && !b._id.includes("_staged"): // awarded
      return BidItemStatus.awarded_in_rank_row;
    case b.assigned && b.awarded && b._id.includes("_staged"): // assigned to unaward STAGED ROW
      return BidItemStatus.assigned_to_unaward_in_stage_row;
    case b.assigned && b.awarded && !b._id.includes("_staged"): // assigned to unaward
      return BidItemStatus.assigned_to_unaward_in_rank_row;
    case !b.awarded && !!(b as IDisplayBidPairing)?.bidPairingId && !(b as IDisplayBidPairing)?.eligibleToAssign:
      return BidItemStatus.not_eligible_to_assign;
    default:
      return BidItemStatus.default;
  }
};

export const addPairingToArr = (
  pairing: IDisplayPairing | IDisplayBidPairing,
  y0: number,
  y: number,
  pairingPositions: number[],
  pairingColors: number[],
  nfaPositions: number[],
  nfaColors: number[]
): void => {
  let pairingContainsRemovedFlights: boolean = undefined;

  // OPEN FLYING REQUEST
  const isOpenFlyingRequest = (pairing as any)?.openFlyingRequest?.length > 0;

  // BID PAIRING CONDITIONS
  const renderAsBidPairing = !!(pairing as IDisplayBidPairing)?.bidPairingId;
  const assignedToAward =
    isOpenFlyingRequest ||
    (renderAsBidPairing && returnBidStatus(pairing as IDisplayBidPairing) === BidItemStatus.assigned_in_rank_row);
  const assignedToUnaward =
    renderAsBidPairing &&
    returnBidStatus(pairing as IDisplayBidPairing) === BidItemStatus.assigned_to_unaward_in_rank_row;
  const assignedBid = assignedToAward || assignedToUnaward;
  const ineligibleToAward =
    renderAsBidPairing && returnBidStatus(pairing as IDisplayBidPairing) === BidItemStatus.not_eligible_to_assign;
  // const awardedBidPairing = renderAsBidPairing && (pairing as IDisplayBidPairing)?.assigned && !(pairing as IDisplayBidPairing)?.awarded;

  pairing.eventGroups.forEach((eventGroup: IDisplayPairingEventGroup) => {
    // Apply a 5 pixel top and bottom margin to normal pairings / 1 to bid pairings.
    const eventTopBottomMargin = renderAsBidPairing ? 2 : 5;
    {
      const nfaY0 = y0 + eventTopBottomMargin;
      const nfaY1 = y - eventTopBottomMargin;
      eventGroup?.nonFlyingAssignments?.forEach((nonFlyingAssignment: INonFlyingPairingAssignment) => {
        const x0 = new Date(nonFlyingAssignment.start).getTime() - Camera.referenceTime;
        const x1 = new Date(nonFlyingAssignment.end).getTime() - Camera.referenceTime;
        const positions = [x0, nfaY0, x1, nfaY0, x1, nfaY1, x0, nfaY0, x1, nfaY1, x0, nfaY1];
        nfaPositions.push(...positions);
        const rgba = hexToIRGBA(nonFlyingAssignment.assignmentType.defaultColor);
        const baseAlpha = nonFlyingAssignment.assignmentType.defaultColorAlpha ?? rgba.alpha;
        const alpha = assignedBid ? 0.25 : baseAlpha;
        const color = [rgba.red, rgba.green, rgba.blue, alpha];
        nfaColors.push(...color, ...color, ...color, ...color, ...color, ...color);
      });
    }
    {
      const yStart = y0 + eventTopBottomMargin;
      const yEnd = y - eventTopBottomMargin;
      eventGroup.flights.forEach((pairingFlight: IPairingFlight) => {
        const x0 = pairingFlight.actualOut - Camera.referenceTime;
        const x1 = pairingFlight.actualIn - Camera.referenceTime;
        const positions = [x0, yStart, x1, yStart, x1, yEnd, x0, yStart, x1, yEnd, x0, yEnd];
        nfaPositions.push(...positions);
        const rgba = hexToIRGBA(pairingFlight.flightType.color);
        const color = [rgba.red, rgba.green, rgba.blue, assignedBid || ineligibleToAward ? 0.25 : rgba.alpha];
        nfaColors.push(...color, ...color, ...color, ...color, ...color, ...color);
        if (pairingFlight.removalType) {
          pairingContainsRemovedFlights = true;
        }
      });

      eventGroup.positioningFlights.forEach((positioningFlight: IDisplayPositioningFlight) => {
        const x0 = positioningFlight.outTime - Camera.referenceTime;
        const x1 = positioningFlight.inTime - Camera.referenceTime;
        const positions = [x0, yStart, x1, yStart, x1, yEnd, x0, yStart, x1, yEnd, x0, yEnd];
        nfaPositions.push(...positions);
        // const rgba = hexToIRGBA(positioningFlight..color);
        const color = [1.0, 0.0, 1.0, assignedBid || ineligibleToAward ? 0.25 : 1.0];
        nfaColors.push(...color, ...color, ...color, ...color, ...color, ...color);
        if (positioningFlight.removalType) {
          pairingContainsRemovedFlights = true;
        }
      });

      eventGroup.groundEvents?.forEach((groundEvent: IPairingGroundEvent) => {
        const x0 = new Date(groundEvent.actualStart)?.valueOf() - Camera.referenceTime;
        const x1 = new Date(groundEvent.actualEnd)?.valueOf() - Camera.referenceTime;
        const positions = [x0, yStart, x1, yStart, x1, yEnd, x0, yStart, x1, yEnd, x0, yEnd];
        nfaPositions.push(...positions);
        const rgba = hexToIRGBA(groundEvent.eventType.color);
        const color = [rgba.red, rgba.green, rgba.blue, assignedBid || ineligibleToAward ? 0.25 : rgba.alpha];
        nfaColors.push(...color, ...color, ...color, ...color, ...color, ...color);
      });
    }
  });
  {
    const x0 = pairing.start?.valueOf() - Camera.referenceTime;
    const x1 = pairing.end?.valueOf() - Camera.referenceTime;

    const positions = [x0, y0, x1, y0, x1, y, x0, y0, x1, y, x0, y];
    pairingPositions.push(...positions);

    switch (true) {
      case pairingContainsRemovedFlights:
        pairingColors.push(...ghostPairingColors); // If pairings contains missing flights, they will render with a light grey background.
        break;
      case ineligibleToAward: // If bid pairing is awarded to someone else not eligible to bid on, display as disabled.
        pairingColors.push(...ineligibleToAwardColors);
        break;
      case assignedToUnaward:
      case assignedToAward:
        pairingColors.push(...tempPairingColors); // Render as semi-transparent.
        break;
      default:
        pairingColors.push(...pColors);
    }
  }
};

/**
 * Bid Pairings can include duplicates assigned to different ranks, and/or positionBid.
 * To support this, include pref rank and position bId on as part of the pairing id in the buffers.
 * @param bidPairings -
 * @returns
 */
const includeRankPositionInBidPairingID = (bidPairings: IDisplayBidPairing[]): IDisplayBidPairing[] => {
  return bidPairings?.map((bp) => {
    const bidPairingIdRank = `${bp._id}_rank${bp.prefRank}_positionBidOn${bp.positionBidOn}`;
    return { ...bp, _id: bidPairingIdRank };
  });
};

export class PairingsGL {
  // Pairings
  public static pairingYStart = 12;
  public static pairingHeight = 24;
  private pairingData: IDisplayPairing[];
  private pairingBuffer: PairingBuffer;
  private pairingOffsets: IPairingVertexOffset = {};
  private vertexCount = 0;
  private nfaVertexCount = 0;
  private eventBuffer: PairingBuffer;
  private temporaryPairingBuffer: PairingBuffer;
  private temporaryPairingVertexCount = 0;
  public lastModified = 0;
  // Unassigned Pairings rendered on crew rows
  private unassignedPairingData: IDisplayPairing[];
  private unassignedPairingBuffer: PairingBuffer;
  private unassignedPairingOffsets: IPairingVertexOffset = {};
  private unassignedPairingVertexCount = 0;
  private unassignedNfaVertexCount = 0;
  private unassignedEventBuffer: PairingBuffer;
  public unassignedPairingslastModified = 0;
  // Bid Pairings
  private bidPairingData: IDisplayBidPairing[];
  private bidPairingBuffer: PairingBuffer;
  private bidPairingOffsets: IPairingVertexOffset = {};
  private bidPairingVertexCount = 0;
  private bidNfaVertexCount = 0;
  private bidEventBuffer: PairingBuffer;
  public bidPairingslastModified = 0;
  // Bid Pref Rank backgrounds
  private bidPrefRowsBuffer: PairingBuffer;
  private bidPrefRowsVertexCount = 0;

  // shared
  public range: IIndexRange;

  constructor(gl: WebGLRenderingContext, pairingShader: PairingShader) {
    // Create Pairing Buffers
    this.pairingBuffer = new PairingBuffer(gl, pairingShader);
    this.eventBuffer = new PairingBuffer(gl, pairingShader);
    this.temporaryPairingBuffer = new PairingBuffer(gl, pairingShader);
    // Create Unassigned Pairing Buffers
    this.unassignedPairingBuffer = new PairingBuffer(gl, pairingShader);
    this.unassignedEventBuffer = new PairingBuffer(gl, pairingShader);
    // Create Bid Pairing Buffers
    this.bidPairingBuffer = new PairingBuffer(gl, pairingShader);
    this.bidEventBuffer = new PairingBuffer(gl, pairingShader);
    this.bidPrefRowsBuffer = new PairingBuffer(gl, pairingShader);
  }

  public updateBidPairingBuffers(gl: WebGLRenderingContext, bidPairings: IDisplayBidPairing[]): void {
    const bidPairingsRank = includeRankPositionInBidPairingID(bidPairings);
    this.bidPairingData = bidPairingsRank;

    const pairingPositions: number[] = [];
    const pairingColors: number[] = [];
    const nfaPositions: number[] = [];
    const nfaColors: number[] = [];

    bidPairingsRank.forEach((bidPairing: IDisplayBidPairing) => {
      const stagingRowHeight = crewBidRowHeight + 4;
      const y0 =
        PairingsGL.pairingYStart +
        28 +
        stagingRowHeight +
        bidPairing.prefRank * crewBidRowHeight +
        bidPairing.prefRank * 4;
      const y = y0 + crewBidRowHeight;

      if (this.bidPairingOffsets[bidPairing._id]) {
        this.bidPairingOffsets[bidPairing._id].pairingOffset = pairingPositions.length / 2;
        this.bidPairingOffsets[bidPairing._id].nfaOffset = nfaPositions.length / 2;
      } else {
        this.bidPairingOffsets[bidPairing._id] = {
          pairingOffset: pairingPositions.length / 2,
          pairingVertexCount: 0,
          nfaOffset: nfaPositions.length / 2,
          nfaVertexCount: 0,
        };
      }

      addPairingToArr(bidPairing, y0, y, pairingPositions, pairingColors, nfaPositions, nfaColors);
      const isAssignedOrAwarded =
        (bidPairing?.assigned && !bidPairing?.awarded) || (!bidPairing?.assigned && bidPairing?.awarded);
      // Bid pairing is assigned or awarded, create a copy of it in the buffer and render this one in the staging row.
      if (isAssignedOrAwarded) {
        const assignedBidPairing = { ...bidPairing, _id: `${bidPairing._id}_staged` };
        const y0 = PairingsGL.pairingYStart + 28 + stagingRowHeight;
        const y = y0 + crewBidRowHeight;
        if (this.bidPairingOffsets[assignedBidPairing._id]) {
          this.bidPairingOffsets[assignedBidPairing._id].pairingOffset = pairingPositions.length / 2;
          this.bidPairingOffsets[assignedBidPairing._id].nfaOffset = nfaPositions.length / 2;
        } else {
          this.bidPairingOffsets[assignedBidPairing._id] = {
            pairingOffset: pairingPositions.length / 2,
            pairingVertexCount: 0,
            nfaOffset: nfaPositions.length / 2,
            nfaVertexCount: 0,
          };
        }
        addPairingToArr(assignedBidPairing, y0, y, pairingPositions, pairingColors, nfaPositions, nfaColors);
      }

      this.bidPairingslastModified = Date.now();
      //keep track of vertexCount
      this.bidPairingOffsets[bidPairing._id].nfaVertexCount = nfaPositions.length / 2;
      this.bidPairingOffsets[bidPairing._id].pairingVertexCount = pairingPositions.length / 2;
    });
    //console.log(pairingColors);
    this.bidPairingBuffer.updateBuffers(gl, pairingPositions, pairingColors);
    this.bidEventBuffer.updateBuffers(gl, nfaPositions, nfaColors);
    this.bidPairingVertexCount = pairingPositions.length / 2;
    this.bidNfaVertexCount = nfaPositions.length / 2;
  }

  public updateBidPrefRankBuffer(
    gl: WebGLRenderingContext,
    bidRankSettings: [{ prefRank: number; label: string; color: string }] | null,
    start: number,
    end: number
  ): void {
    //console.log(gl, bidRankSettings);
    //console.log(start, end);
    const rankPositions: number[] = [];
    const rankColors: number[] = [];
    const x0 = start - Camera.referenceTime;
    const x1 = end - Camera.referenceTime;

    // Add Staging Row
    const stagingRowHeight = crewBidRowHeight + 4;
    const stagingY0 = PairingsGL.pairingYStart + 28 + stagingRowHeight;
    const stagingY = stagingY0 + crewBidRowHeight;
    const stagingRowPositions = [x0, stagingY0, x1, stagingY0, x1, stagingY, x0, stagingY0, x1, stagingY, x0, stagingY];
    const stagingRowDarkenColor = LightenDarkenColor("#88EC8C", -0.75); // Dark bid row backgrounds so pairings stand out more.
    const stagingRowRgba = hexToIRGBA(stagingRowDarkenColor);
    const stagingRowColor = [stagingRowRgba.red, stagingRowRgba.green, stagingRowRgba.blue, stagingRowRgba.alpha];
    rankPositions.push(...stagingRowPositions);
    rankColors.push(
      ...stagingRowColor,
      ...stagingRowColor,
      ...stagingRowColor,
      ...stagingRowColor,
      ...stagingRowColor,
      ...stagingRowColor
    );

    // Add pref rank Rows.
    for (const rank of bidRankSettings) {
      const y0 =
        PairingsGL.pairingYStart + 28 + stagingRowHeight + rank.prefRank * crewBidRowHeight + rank.prefRank * 4;
      const y = y0 + crewBidRowHeight;
      const positions = [x0, y0, x1, y0, x1, y, x0, y0, x1, y, x0, y];
      const darkenColor = LightenDarkenColor(rank.color, -0.75); // Dark bid row backgrounds so pairings stand out more.
      const rgba = hexToIRGBA(darkenColor);
      const color = [rgba.red, rgba.green, rgba.blue, rgba.alpha];
      rankPositions.push(...positions);
      rankColors.push(...color, ...color, ...color, ...color, ...color, ...color);
    }
    this.bidPrefRowsBuffer.updateBuffers(gl, rankPositions, rankColors);
    this.bidPrefRowsVertexCount = rankPositions.length / 2;
  }

  public updateUnassignedPairingBuffers(gl: WebGLRenderingContext, unassignedPairings: IDisplayPairing[]): void {
    this.unassignedPairingData = unassignedPairings;
    const pairingPositions: number[] = [];
    const pairingColors: number[] = [];
    // iterating over an aircraft (row) so height will always be same
    const y0 = PairingsGL.pairingYStart;
    const y = y0 + PairingsGL.pairingHeight;
    const nfaPositions: number[] = [];
    const nfaColors: number[] = [];
    unassignedPairings.forEach((pairing: IDisplayPairing) => {
      if (this.unassignedPairingOffsets[pairing._id]) {
        this.unassignedPairingOffsets[pairing._id].pairingOffset = pairingPositions.length / 2;
        this.unassignedPairingOffsets[pairing._id].nfaOffset = nfaPositions.length / 2;
      } else {
        this.unassignedPairingOffsets[pairing._id] = {
          pairingOffset: pairingPositions.length / 2,
          pairingVertexCount: 0,
          nfaOffset: nfaPositions.length / 2,
          nfaVertexCount: 0,
        };
      }
      addPairingToArr(pairing, y0, y, pairingPositions, pairingColors, nfaPositions, nfaColors);
      this.lastModified = Date.now();
      //keep track of vertexCount
      this.unassignedPairingOffsets[pairing._id].nfaVertexCount = nfaPositions.length / 2;
      this.unassignedPairingOffsets[pairing._id].pairingVertexCount = pairingPositions.length / 2;
    });

    this.unassignedPairingBuffer.updateBuffers(gl, pairingPositions, pairingColors);
    this.unassignedEventBuffer.updateBuffers(gl, nfaPositions, nfaColors);
    this.unassignedPairingVertexCount = pairingPositions.length / 2;
    this.unassignedNfaVertexCount = nfaPositions.length / 2;
  }

  public updatePairingBuffers(gl: WebGLRenderingContext, pairings: IDisplayPairing[]): void {
    // this.flightIndexesInRange = [0, flightData.length - 1];
    this.pairingData = pairings;
    const pairingPositions: number[] = [];
    const pairingColors: number[] = [];
    // iterating over an aircraft (row) so height will always be same
    const y0 = PairingsGL.pairingYStart;
    const y = y0 + PairingsGL.pairingHeight;

    const nfaPositions: number[] = [];
    const nfaColors: number[] = [];
    pairings.forEach((pairing: IDisplayPairing) => {
      if (this.pairingOffsets[pairing._id]) {
        this.pairingOffsets[pairing._id].pairingOffset = pairingPositions.length / 2;
        this.pairingOffsets[pairing._id].nfaOffset = nfaPositions.length / 2;
      } else {
        this.pairingOffsets[pairing._id] = {
          pairingOffset: pairingPositions.length / 2,
          pairingVertexCount: 0,
          nfaOffset: nfaPositions.length / 2,
          nfaVertexCount: 0,
        };
      }

      addPairingToArr(pairing, y0, y, pairingPositions, pairingColors, nfaPositions, nfaColors);

      this.lastModified = Date.now();
      //keep track of vertexCount
      this.pairingOffsets[pairing._id].nfaVertexCount = nfaPositions.length / 2;
      this.pairingOffsets[pairing._id].pairingVertexCount = pairingPositions.length / 2;
    });

    this.pairingBuffer.updateBuffers(gl, pairingPositions, pairingColors);
    this.eventBuffer.updateBuffers(gl, nfaPositions, nfaColors);
    this.vertexCount = pairingPositions.length / 2;
    this.nfaVertexCount = nfaPositions.length / 2;
  }

  public updateTempPairingsBuffer(gl: WebGLRenderingContext, temporaryPairings: ITemporaryPairing[]): void {
    const pairingPositions: number[] = [];
    const pairingColors: number[] = [];
    // iterating over an aircraft (row) so height will always be same
    const y0 = PairingsGL.pairingYStart;
    const y = y0 + PairingsGL.pairingHeight;
    for (const temporaryPairing of temporaryPairings) {
      const x0 = temporaryPairing.start - Camera.referenceTime;
      const x1 = temporaryPairing.end - Camera.referenceTime;

      const positions = [x0, y0, x1, y0, x1, y, x0, y0, x1, y, x0, y];

      pairingPositions.push(...positions);
      pairingColors.push(...tempPairingColors);
    }
    this.temporaryPairingBuffer.updateBuffers(gl, pairingPositions, pairingColors);
    this.temporaryPairingVertexCount = pairingPositions.length / 2;
  }

  public getLogicModifiers(): IPairingsLogicModifierGL[] {
    const logicModifiers: IPairingsLogicModifierGL[] = [];
    for (let i = this.range.minIndex; i <= this.range.maxIndex; i++) {
      const pairing = this.pairingData[i];
      if (pairing?.logicModifiers) {
        logicModifiers.push({
          pairingStart: pairing?.start?.valueOf(),
          logicModifiers: pairing?.logicModifiers,
          pairingEnd: pairing?.end?.valueOf(),
        });
      }
    }
    return logicModifiers;
  }

  public getcheckInOuts(): ICheckInOutOverride[] {
    const checkOverrides: ICheckInOutOverride[] = [];
    for (let i = this.range.minIndex; i <= this.range.maxIndex; i++) {
      const pairing = this.pairingData[i];
      if (pairing?.checkInOutOverrides) {
        checkOverrides.push(...pairing.checkInOutOverrides);
      }
    }
    return checkOverrides;
  }

  public draw = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    if (this.vertexCount !== 0) {
      const range = getPairingsInRange(camera, this.pairingData);
      this.range = range;
      const minPairingId = this.pairingData[range.minIndex]?._id;
      const maxPairingId = this.pairingData[range.maxIndex]?._id;

      const pairngOffset = this.pairingOffsets[minPairingId]?.pairingOffset;
      const pairngVC = this.pairingOffsets[maxPairingId]?.pairingVertexCount - pairngOffset;

      // const offset = range.minIndex * 6;
      // const numVerts = (range.maxIndex + 1) * 6 - offset + 6;

      if (pairngVC > 0 && pairngVC <= this.vertexCount) {
        this.pairingBuffer.draw(gl, camera, yStart, -2, pairngOffset, pairngVC);
      }

      const nfaOffset = this.pairingOffsets[minPairingId] ? this.pairingOffsets[minPairingId].nfaOffset : 0;
      const nfaVC = this.pairingOffsets[maxPairingId]?.nfaVertexCount - nfaOffset;

      if (this.nfaVertexCount > 0) {
        this.eventBuffer.draw(gl, camera, yStart, -1.9, nfaOffset, nfaVC);
      }
    }
    if (this.temporaryPairingVertexCount > 0) {
      this.temporaryPairingBuffer.draw(gl, camera, yStart, -1.9, 0, this.temporaryPairingVertexCount);
    }
  };

  public drawUnassignedPairings = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    if (this.unassignedPairingVertexCount !== 0) {
      const range = getPairingsInRange(camera, this.unassignedPairingData);
      this.range = range;
      const minPairingId = this.unassignedPairingData[range.minIndex]?._id;
      const maxPairingId = this.unassignedPairingData[range.maxIndex]?._id;
      const pairngOffset = this.unassignedPairingOffsets[minPairingId]?.pairingOffset;
      const pairngVC = this.unassignedPairingOffsets[maxPairingId]?.pairingVertexCount - pairngOffset;

      if (pairngVC > 0 && pairngVC <= this.unassignedPairingVertexCount) {
        this.unassignedPairingBuffer.draw(gl, camera, yStart, -2, pairngOffset, pairngVC);
      }

      const nfaOffset = this.unassignedPairingOffsets[minPairingId]
        ? this.unassignedPairingOffsets[minPairingId].nfaOffset
        : 0;
      const nfaVC = this.unassignedPairingOffsets[maxPairingId]?.nfaVertexCount - nfaOffset;

      if (this.unassignedNfaVertexCount > 0) {
        this.unassignedEventBuffer.draw(gl, camera, yStart, -1.9, nfaOffset, nfaVC);
      }
    }
  };

  public drawBidRankBackgrounds = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    // Draw pref rank background rows (set Zpos to -5 which should ensure it's behind all important content).
    if (this.bidPrefRowsVertexCount > 0) {
      this.bidPrefRowsBuffer.draw(gl, camera, yStart, -5, 0, this.bidPrefRowsVertexCount);
    }
  };

  public drawBidPairings = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    // Draw bid pairings and their events
    if (this.bidPairingVertexCount !== 0) {
      const range = getPairingsInRange(camera, this.bidPairingData);
      this.range = range;
      const minBidPairingId = this.bidPairingData[range.minIndex]?._id;
      const maxBidPairingId = this.bidPairingData[range.maxIndex]?._id;

      const bidPairngOffset = this.bidPairingOffsets[minBidPairingId]?.pairingOffset;
      const bidPairngVC = this.bidPairingOffsets[maxBidPairingId]?.pairingVertexCount - bidPairngOffset;

      // Draw bid pairing backgrounds (grey)
      if (bidPairngVC > 0 && bidPairngVC <= this.bidPairingVertexCount) {
        this.bidPairingBuffer.draw(gl, camera, yStart, -2, bidPairngOffset, bidPairngVC);
      }

      const bidNfaOffset = this.bidPairingOffsets[minBidPairingId]
        ? this.bidPairingOffsets[minBidPairingId].nfaOffset
        : 0;
      const bidNfaVC = this.bidPairingOffsets[maxBidPairingId]?.nfaVertexCount - bidNfaOffset;

      // Draw bid pairing events
      if (this.bidNfaVertexCount > 0) {
        this.bidEventBuffer.draw(gl, camera, yStart, -1.9, bidNfaOffset, bidNfaVC);
      }
    }
  };
}
